<script>
export default {
  name: "OrderStartCard",
  props: {
    selectedOrder: {
      type: Object,
      default: null
    },
    canStartByHbl: {
      type: Boolean,
      default: false
    },
    hblStartInput: {
      type: String,
      default: ""
    },
    isComposing: {
      type: Boolean,
      default: false
    },
    isStartingPacking: {
      type: Boolean,
      default: false
    },
    isSubmittingWaybill: {
      type: Boolean,
      default: false
    },
    canCompleteBackup: Boolean,
    skuRecoveryBlocked: Boolean,
    isSubmittingBackupCompletion: Boolean,
    isExpressMode: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    "update:hblStartInput",
    "update:isComposing",
    "start-packing",
    "print-waybill",
    "download-waybill",
    "complete-backup"
  ],
  computed: {
    selectionReasonItems() {
      return String(this.selectedOrder?.selectionReason || "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const separatorIndex = line.indexOf(":")
          if (separatorIndex < 0) {
            return {
              label: "선택근거",
              value: line
            }
          }
          return {
            label: line.slice(0, separatorIndex).trim(),
            value: line.slice(separatorIndex + 1).trim()
          }
        })
    }
  },
  methods: {
    onHblInput(value) {
      this.$emit("update:hblStartInput", value)
    },
    onStartPacking() {
      this.$emit("start-packing")
    },
    onPrintWaybill() {
      this.$emit("print-waybill")
    },
    onCompleteBackup() {
      this.$emit("complete-backup")
    },
    onDownloadWaybill() {
      this.$emit("download-waybill")
    }
  }
}
</script>

<template>
  <div class="comp-box" :class="{ 'order-start--express': isExpressMode }">
    <div class="row items-center q-mb-sm">
      <div class="col">
        <h6 class="section-title q-mt-none q-mb-none">
          2) {{ isExpressMode ? "익스프레스 자동 처리 결과" : "오더 자동 선택 + 포장 시작" }}
        </h6>
      </div>
      <div class="col-auto">
        <div class="row q-gutter-sm">
          <q-btn
            color="grey-8"
            class="compact-btn"
            outline
            label="PDF 확인"
            :disable="skuRecoveryBlocked || !selectedOrder || isSubmittingWaybill || isStartingPacking || isSubmittingBackupCompletion"
            :loading="isSubmittingWaybill || isStartingPacking"
            @click="onDownloadWaybill"
          />
          <q-btn
            color="primary"
            class="compact-btn"
            label="운송장 출력"
            :disable="skuRecoveryBlocked || !selectedOrder || isSubmittingWaybill || isStartingPacking || isSubmittingBackupCompletion"
            :loading="isSubmittingWaybill || isStartingPacking"
            @click="onPrintWaybill"
          />
          <q-btn
            color="positive"
            class="compact-btn"
            outline
            label="임시콘솔 + 포장완료"
            :disable="!canCompleteBackup || isSubmittingBackupCompletion"
            :loading="isSubmittingBackupCompletion"
            @click="onCompleteBackup"
          />
        </div>
      </div>
    </div>
    <div v-if="selectedOrder" class="row q-col-gutter-sm">
      <div class="col-12">
        <div class="meta-grid">
          <div class="meta-row">
            <div class="meta-key">오더번호</div>
            <div class="meta-value">{{ selectedOrder.orderNo }}</div>
          </div>
          <div class="meta-row">
            <div class="meta-key">HBL</div>
            <div class="meta-value">{{ selectedOrder.hblNo }}</div>
          </div>
          <div class="meta-row">
            <div class="meta-key">고객명</div>
            <div class="meta-value">{{ selectedOrder.customerName }}</div>
          </div>
          <div class="meta-row">
            <div class="meta-key">목적지</div>
            <div class="meta-value">{{ selectedOrder.destination }}</div>
          </div>
          <div class="meta-row">
            <div class="meta-key">생성시각</div>
            <div class="meta-value">{{ selectedOrder.createdAt }}</div>
          </div>
          <div class="meta-row selection-reason">
            <div class="meta-key">선택근거</div>
            <div v-if="selectionReasonItems.length" class="selection-reason-box">
              <div
                v-for="item in selectionReasonItems"
                :key="`${item.label}-${item.value}`"
                class="selection-reason-row"
              >
                <span class="selection-reason-label">{{ item.label }}</span>
                <span class="selection-reason-value">{{ item.value || "-" }}</span>
              </div>
            </div>
            <div v-else class="meta-value">-</div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-grey-7">
      아직 선택된 오더가 없습니다.
    </div>

    <q-separator class="q-my-md" />

    <div v-if="isExpressMode" class="express-order-guide">
      <q-icon name="bolt" size="20px" />
      <div>
        <div class="express-order-guide__title">HBL 스캔 단계 없음</div>
        <div class="express-order-guide__desc">
          익스프레스 모드에서는 선택된 오더가 운송장 출력 후 자동으로 포장완료됩니다.
        </div>
      </div>
    </div>

    <div v-else>
      <div class="text-subtitle2 q-mb-sm">HBL 1차 스캔으로 포장 시작</div>
      <q-input
        ref="hblStartInputRef"
        :model-value="hblStartInput"
        label="HBL 1차 스캔"
        :disable="!canStartByHbl"
        @update:model-value="onHblInput"
        @keyup.enter="onStartPacking"
        @compositionstart="$emit('update:isComposing', true)"
        @compositionend="$emit('update:isComposing', false)"
      >
        <template v-slot:append>
          <q-btn
            color="secondary"
            label="포장 시작"
            :disable="!canStartByHbl || isStartingPacking"
            :loading="isStartingPacking"
            @click="onStartPacking"
          />
        </template>
      </q-input>
    </div>
  </div>
</template>

<style scoped lang="scss">
.section-title {
  font-size: 1rem;
  font-weight: 700;
}

.meta-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.meta-row {
  display: grid;
  grid-template-columns: 86px minmax(0, 1fr);
  align-items: start;
  column-gap: 10px;
  font-size: 14px;
}

.meta-key {
  min-height: 24px;
  padding: 3px 8px;
  border-radius: 4px;
  background: #eef3f8;
  color: #4b5d70;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
}

.meta-value {
  min-width: 0;
  color: #172b3f;
  font-weight: 600;
  line-height: 1.7;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.selection-reason-box {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.selection-reason-row {
  display: grid;
  grid-template-columns: 78px minmax(0, 1fr);
  align-items: start;
  gap: 8px;
}

.selection-reason-label {
  padding: 2px 6px;
  border-radius: 4px;
  background: #eef3f8;
  color: #4b5d70;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  text-align: center;
}

.selection-reason-value {
  min-width: 0;
  color: #172b3f;
  font-weight: 600;
  line-height: 1.45;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.compact-btn {
  min-height: 34px;
}

.order-start--express {
  border-left: 5px solid #ef6c00;
}

.express-order-guide {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #f4c281;
  border-radius: 6px;
  background: #fffaf0;
  color: #633b0d;
}

.express-order-guide__title {
  font-size: 13px;
  font-weight: 800;
}

.express-order-guide__desc {
  margin-top: 2px;
  font-size: 12px;
  line-height: 1.5;
}
</style>
